import React, { CSSProperties, forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { isNull } from 'lodash';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { AccessPolicy } from 'common/assets/Assets';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import localIdMap from 'common/upload/localIdMap';
import { cancelUploadFileAsset, isUploadCancelError, uploadFileAsset, UploadedFileAsset } from 'common/upload/Upload';
import { ErrorModal, InvalidFile, ErrorCode, InvalidFileError } from 'common/upload/uploader/error-modal/ErrorModal';

import 'common/upload/uploader/Uploader.scss';

export interface OnFilesAcceptedFunc {
    (currentAcceptedFiles: (File | UploadedFileAsset)[], allFiles: (File | UploadedFileAsset)[]): void;
}

export interface OnFilesRejectedFunc {
    (currentRejectedFiles: InvalidFile[], allFiles: (File | UploadedFileAsset)[]): void;
}

export interface RemoveFileFunc {
    (index: number): (File | UploadedFileAsset)[];
}

interface ClearFilesFunc {
    (): (File | UploadedFileAsset)[];
}

interface AddAcceptedFilesFunc {
    (files: (File | UploadedFileAsset)[]): void;
}

interface AddRejectedFilesFunc {
    (files: InvalidFile[]): void;
}

export interface UploaderHandles {
    removeFile: RemoveFileFunc;
    clearFiles: ClearFilesFunc;
    addAcceptedFiles: AddAcceptedFilesFunc;
    addRejectedFiles: AddRejectedFilesFunc;
}

export interface ValidateUploadedFilesFunc {
    (files: UploadedFileAsset): Promise<{
        isValid: boolean;
        errors?: InvalidFileError[];
    }>;
}

export interface Props {
    children: React.ReactNode;
    agencyId: number;
    tenantId: number;
    /** custom classNames */
    className?: string;
    /** default media files */
    media?: (File | UploadedFileAsset)[];
    moduleId?: string;
    /** default: null */
    expireDate?: number | null;
    /** supported file formats */
    accept?: string;
    /** maximun number of files that can be added, default: 10 */
    maxFiles?: number;
    /** minimum size required per file (in bytes), default: 0 */
    minSizePerFile?: number;
    /** maximum size limit per file (in bytes) */
    maxSizePerFile?: number | undefined;
    /** total size limit for all files (in bytes), default: null (no restrictions) */
    maxSizeTotal?: number | null;
    multiple?: boolean;
    /** disable click */
    noClick?: boolean;
    /** disable drag/drop */
    noDrag?: boolean;
    noDragEventsBubbling?: boolean;
    /** maintain context of all the files, call  ** clearFiles ** to begin new context, default: true  */
    trackAllFiles?: boolean;
    disabled?: boolean;
    /** enable/disable default ui when drag is active, default: true */
    showDefaultDragActive?: boolean;
    /** provide className to be applied to dropzone, will not be applied if noDrag: true  */
    dropzoneClassName?: string;
    /** provide css styles to be applied to dropzone, will not be applied if noDrag: true  */
    dropzoneStyle?: CSSProperties;
    /** provide className to be applied to dropzone when drag is active, will not be applied if noDrag: true  */
    dragActiveClassName?: string;
    /** provide css styles to be applied to dropzone when drag is active, will not be applied if noDrag: true  */
    dragActiveStyle?: CSSProperties;
    /** display modal with errors for Rejected files, default: true */
    displayErrorModal?: boolean;
    /** display file upload progress, will only be shown when uploadToCloud: true, default: false */
    displayProgressBar?: boolean;
    /** directly upload to cloud (s3), default: false */
    uploadToCloud?: boolean;
    cancelUpload?: boolean;
    /** default: public */
    accessPolicy?: AccessPolicy;
    /** In case of nested Uploaders, provide ref of inner uploader */
    innerUploaderRef?: any;
    shortenExternalUrl?: any;
    /** custom validation function which will be invoked once file is uploaded, needs uploadToCloud: true */
    validateUploadedFile?: ValidateUploadedFilesFunc;
    onFilesAccepted?: OnFilesAcceptedFunc;
    onFilesRejected?: OnFilesRejectedFunc;
    onUploadProgress?: any;
    onUploadCancel?: any;
    disableMessageDispatch?: boolean;
    /** if true, will not show success message when uploading is successful */
    isClosedTicket?: boolean;
    /** callback function after files are uploaded and validated */
    onUploadSuccess?: () => void;
    hidden?: boolean;
}

export const Uploader = forwardRef<UploaderHandles, Props>(
    (
        {
            children,
            agencyId,
            tenantId,
            className,
            media,
            moduleId,
            expireDate,
            accept,
            maxFiles,
            minSizePerFile,
            maxSizePerFile,
            maxSizeTotal,
            multiple,
            noClick,
            noDrag,
            noDragEventsBubbling,
            trackAllFiles,
            disabled,
            dropzoneClassName,
            dropzoneStyle,
            dragActiveClassName,
            dragActiveStyle,
            showDefaultDragActive,
            displayErrorModal,
            displayProgressBar,
            innerUploaderRef,
            uploadToCloud,
            cancelUpload,
            accessPolicy,
            shortenExternalUrl,
            validateUploadedFile,
            onFilesAccepted,
            onFilesRejected,
            onUploadProgress,
            onUploadCancel,
            disableMessageDispatch,
            isClosedTicket = false,
            hidden = false,
            onUploadSuccess,
        }: Props,
        ref: any,
    ) => {
        let localIdCounter = 0;
        const [, dispatchMessage] = useMessage();
        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            accept,
            maxFiles,
            minSize: minSizePerFile,
            maxSize: maxSizePerFile,
            multiple,
            noClick,
            noDrag,
            noDragEventsBubbling,
            disabled,
            onDropAccepted: (files) => {
                if (innerUploaderRef && innerUploaderRef.current) {
                    innerUploaderRef.current.addAcceptedFiles(files);
                } else {
                    handleAcceptedFiles(files);
                }
            },
            onDropRejected: (files) => {
                if (innerUploaderRef && innerUploaderRef.current) {
                    innerUploaderRef.current.addRejectedFiles(files);
                } else {
                    handleRejectedFiles(files);
                }
            },
        });
        const { t } = useTranslation(['common/upload']);
        const [uploadedFiles, setUploadedFiles] = useState<(File | UploadedFileAsset)[]>([]);
        const [totalUploadedFilesSize, setTotalUploadedFilesSize] = useState<number | null>(null);
        // const [displayModal, setDisplayModal] = useState<boolean>(false);
        // const [rejectedFiles, setRejectedFiles] = useState<InvalidFile[]>([]);
        const [fileUploadProgress, setFileUploadProgress] = useState<number>(0);
        const [displayModal, setDisplayModal] = useState<boolean>(false);
        const [rejectedFiles, setRejectedFiles] = useState<InvalidFile[]>([]);

        useImperativeHandle(ref, () => ({
            removeFile: (index: number): (File | UploadedFileAsset)[] => {
                if (trackAllFiles) {
                    const allFiles = [...uploadedFiles];

                    if (!isNull(maxSizeTotal)) {
                        setTotalUploadedFilesSize(totalUploadedFilesSize! - allFiles[index].size);
                    }
                    allFiles.splice(index, 1);
                    // update internal list directly and notify parent with the updated list
                    setUploadedFiles(allFiles);
                    if (onFilesAccepted) {
                        onFilesAccepted([], allFiles);
                    }
                    return allFiles;
                }
                return [];
            },
            clearFiles: (): [] => {
                setUploadedFiles([]);
                // setRejectedFiles([]);
                setTotalUploadedFilesSize(0);
                return [];
            },
            addAcceptedFiles: (files: (File | UploadedFileAsset)[]): any => {
                handleAcceptedFiles(files);
            },
            addRejectedFiles: (files: InvalidFile[]): void => {
                handleRejectedFiles(files);
            },
            cancelUpload: (): void => {
                handleUploadCancel();
            },
            // this will only order files in place, do not use this for Upload
            updateUploadedFilesOrder: (files: (File | UploadedFileAsset)[]): void => {
                setUploadedFiles(files);
            },
        }));

        useEffect(() => {
            if (media && media.length) {
                handleAcceptedFiles(media);
            }
        }, []); // eslint-disable-line react-hooks/exhaustive-deps

        const uploadAndValidate = async (
            fileItem: File | UploadedFileAsset,
        ): Promise<{ file: UploadedFileAsset | File; isValid: boolean | null; errors?: InvalidFileError[] }> => {
            try {
                if (isClosedTicket) {
                    dispatchMessage({
                        type: MessageActionType.ShowError,
                        text: t('CLOSED_TICKET_ATTACHMENT_ERROR'),
                    });
                    return {
                        file: fileItem as File,
                        isValid: false,
                        errors: [],
                    };
                }

                let isValid = true;
                let errors: InvalidFileError[] = [];

                handleUploadProgress(5);
                const fileUpload = await uploadFileAsset({
                    agencyId,
                    tenantId,
                    file: fileItem as File,
                    args: {
                        accessPolicy: accessPolicy!,
                        expireDate: expireDate!,
                        fileName: fileItem.name,
                        moduleId: moduleId!,
                        shortenExternalUrl,
                    },
                    handleUploadProgress,
                });

                if (validateUploadedFile) {
                    const validateResponse = await validateUploadedFile(fileUpload);

                    isValid = validateResponse.isValid;
                    errors = validateResponse.errors || [
                        {
                            code: ErrorCode.Default,
                        },
                    ];
                }

                if (!disableMessageDispatch) {
                    setTimeout(
                        () =>
                            dispatchMessage({
                                type: MessageActionType.ShowSuccess,
                                text: t('ATTACHMENT_UPLODED'),
                            }),
                        2000,
                    );
                }

                if (onUploadSuccess) {
                    onUploadSuccess();
                }

                handleUploadProgress(100);
                return {
                    file: fileUpload,
                    isValid,
                    errors,
                };
            } catch (error) {
                handleUploadProgress(100);
                const isCancelled = isUploadCancelError(error);

                if (!isCancelled) {
                    return {
                        file: fileItem as File,
                        isValid: false,
                        errors: [
                            {
                                code: ErrorCode.Default,
                            },
                        ],
                    };
                }
                return {
                    file: fileItem as File,
                    isValid: null,
                    errors: [],
                };
            }
        };

        const handleUploadProgress = (progress: number): void => {
            let inProgress = true;
            let progressValue = null;

            if (progress < 100) {
                progressValue = progress;
            } else {
                progressValue = 0;
                inProgress = false;
            }
            if (displayProgressBar) {
                setFileUploadProgress(progressValue);
            }
            if (onUploadProgress) {
                onUploadProgress(inProgress, progressValue);
            }
        };

        const handleUploadCancel = (): void => {
            cancelUploadFileAsset();
            handleUploadProgress(100);
            if (onUploadCancel) {
                onUploadCancel();
            }
        };

        const handleAcceptedFiles = async (files: (File | UploadedFileAsset)[]): Promise<any> => {
            const validFiles: (File | UploadedFileAsset)[] = [];
            const invalidFiles: InvalidFile[] = [];
            const promises: Promise<any>[] = [];

            files.forEach((fileItem: File | UploadedFileAsset) => {
                if (!(fileItem as UploadedFileAsset).id && !(fileItem as any).url && !localIdMap.has(fileItem)) {
                    // assign a stable temporary id to local File objects without using ++
                    const idSuffix = localIdCounter;

                    localIdCounter += 1;

                    localIdMap.set(fileItem as object, `local-${Date.now()}-${idSuffix}`);
                }
                if (!maxFiles || uploadedFiles.length < maxFiles!) {
                    if (isNull(maxSizeTotal) || fileItem.size + totalUploadedFilesSize! <= maxSizeTotal!) {
                        if (!isNull(maxSizeTotal)) {
                            setTotalUploadedFilesSize(fileItem.size + totalUploadedFilesSize!);
                        }
                        if (!(fileItem as UploadedFileAsset).id && uploadToCloud) {
                            promises.push(uploadAndValidate(fileItem));
                        } else {
                            validFiles.push(fileItem);
                        }
                    } else {
                        invalidFiles.push({
                            file: fileItem as File,
                            errors: [
                                {
                                    code: ErrorCode.TotalFilesTooLarge,
                                },
                            ],
                        });
                    }
                } else {
                    invalidFiles.push({
                        file: fileItem as File,
                        errors: [
                            {
                                code: ErrorCode.TooManyFiles,
                            },
                        ],
                    });
                }
            });
            const validatedFiles = await Promise.all(promises);

            validatedFiles.forEach((fileItem): void => {
                if (!isNull(fileItem?.isValid)) {
                    if (fileItem?.isValid) {
                        validFiles.push(fileItem.file);
                    } else {
                        invalidFiles.push({
                            file: fileItem?.file,
                            errors: fileItem?.errors,
                        });
                    }
                }
            });
            let allFiles: (File | UploadedFileAsset)[] = [];

            if (trackAllFiles) {
                setUploadedFiles((addedFiles) => {
                    allFiles = [...addedFiles, ...validFiles];
                    return allFiles;
                });
            } else {
                allFiles = [...validFiles];
            }
            if (onFilesAccepted) {
                onFilesAccepted(validFiles, allFiles);
            }
            if (invalidFiles) {
                handleRejectedFiles(invalidFiles);
            }
        };

        const handleRejectedFiles = (files: InvalidFile[]): void => {
            if (files && files.length) {
                setRejectedFiles(files);
                if (displayErrorModal) {
                    setDisplayModal(true);
                }
                if (onFilesRejected) {
                    onFilesRejected(files, uploadedFiles);
                }
            }
        };

        const getClassNames = (): string => {
            const classList: string[] = ['uploader-dropzone'];

            if (!noDrag) {
                if (dropzoneClassName) {
                    classList.push(dropzoneClassName);
                }
                if (isDragActive && dragActiveClassName) {
                    classList.push(dragActiveClassName);
                }
            }
            return classList.join(' ');
        };

        const getStyles = (): CSSProperties => {
            let styles = {};

            if (!noDrag) {
                if (dropzoneStyle) {
                    styles = { ...styles, ...dropzoneStyle };
                }
                if (isDragActive && dragActiveStyle) {
                    styles = { ...styles, ...dragActiveStyle };
                }
            }
            return styles;
        };

        const handleModalClose = (): void => {
            setDisplayModal(false);
            setRejectedFiles([]);
        };

        if (hidden) {
            return <></>;
        }

        return (
            <div aria-hidden="true" role="presentation" className={`r4e-uploader ${className}`}>
                <div
                    {...getRootProps({
                        className: getClassNames(),
                        style: getStyles(),
                    })}
                >
                    <input {...getInputProps()} />
                    {children}
                    {isDragActive && showDefaultDragActive ? (
                        <div className="uploader-drag-active-container">
                            <div>
                                <i className="icon-photo-video" />
                            </div>
                            <div className="media-text">{t('DRAG_MEDIA_ATTACH')}</div>
                        </div>
                    ) : null}
                </div>
                {displayErrorModal && displayModal ? (
                    <ErrorModal
                        show={displayModal}
                        onHide={handleModalClose}
                        accept={accept}
                        maxFiles={maxFiles}
                        minSizePerFile={minSizePerFile}
                        maxSizePerFile={maxSizePerFile}
                        maxSizeTotal={maxSizeTotal}
                        totalUploadedFilesSize={totalUploadedFilesSize}
                        rejectedFiles={rejectedFiles}
                    />
                ) : null}
                {fileUploadProgress > 0 && fileUploadProgress < 100 ? (
                    <>
                        {displayProgressBar ? (
                            <div className="uploader-progress-bar-container">
                                <div className="progress-bar-outer">
                                    <div
                                        className="progress-bar-inner"
                                        style={{
                                            width: `${fileUploadProgress}%`,
                                        }}
                                    />
                                </div>
                                <span className="progress-value">{`${fileUploadProgress.toFixed(2)}%`}</span>
                            </div>
                        ) : null}
                        {cancelUpload ? (
                            <div className="cancel-upload-container">
                                <div
                                    className="cancel-upload-btn"
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleUploadCancel}
                                    onKeyPress={(): void => {
                                        // Do nothing
                                    }}
                                >
                                    Cancel Upload
                                </div>
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        );
    },
);

Uploader.defaultProps = {
    accept: '',
    disabled: false,
    minSizePerFile: 0,
    maxSizeTotal: null,
    multiple: true,
    noClick: false,
    noDrag: false,
    noDragEventsBubbling: true,
    trackAllFiles: true,
    showDefaultDragActive: true,
    displayErrorModal: true,
    displayProgressBar: false,
    uploadToCloud: false,
    cancelUpload: false,
    accessPolicy: AccessPolicy.Public,
    expireDate: null,
    moduleId: '',
};
